import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { useStorefrontTenantSettings } from './useStorefrontTenantSettings'
import type { TenantSettingsResponse } from '@/lib/api/controlPlaneClient'

function SettingRow({
  label,
  description,
  children,
}: {
  label: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between px-4 py-5">
      <div className="flex-1 mr-4">
        <span className="text-sm font-medium">{label}</span>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      {children}
    </div>
  )
}

function Divider() {
  return <div className="border-t border-border mx-4" />
}

export function LoginVerificationSettings() {
  const {
    settings,
    isLoading,
    isError,
    updateAsync,
    isUpdating,
    queryKey,
  } = useStorefrontTenantSettings()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load login settings')
    }
  }, [isError])

  const handleToggle = async (
    field: 'line_only_login' | 'require_phone_otp',
    checked: boolean,
  ) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) => (old ? { ...old, [field]: checked ? 1 : 0 } : old),
    )

    try {
      await updateAsync({ [field]: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  return (
    <Card className="rounded-2xl border-0">
      <CardContent className="p-0">
        <SettingRow
          label="LINE-Only Login"
          description="Hide email/password form, show only LINE login button"
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.line_only_login}
              onCheckedChange={(checked) =>
                handleToggle('line_only_login', checked)
              }
              disabled={isUpdating}
              aria-label="LINE-only login mode"
            />
          )}
        </SettingRow>

        <Divider />

        <SettingRow
          label="Require Phone OTP"
          description="Verify phone number via SMS after LINE login"
        >
          {isLoading ? (
            <Skeleton className="w-9 h-5 rounded-full" />
          ) : (
            <Switch
              checked={!!settings?.require_phone_otp}
              onCheckedChange={(checked) =>
                handleToggle('require_phone_otp', checked)
              }
              disabled={isUpdating}
              aria-label="Require phone OTP verification"
            />
          )}
        </SettingRow>
      </CardContent>
    </Card>
  )
}
